import { VStack, HStack, Text, Box, Badge, IconButton, Spinner, Button } from '@chakra-ui/react'
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogBackdrop,
  DialogActionTrigger,
  DialogPositioner,
} from '@chakra-ui/react'
import { Calendar, ArrowRightLeft, User, Trash2, Plus, File, Download } from 'lucide-react'
import { useUnitMutations, useDeleteUnitMutation } from '../hooks/useUnitMutations'
import { getMutationTypeMetadata, formatOwnerName, formatShare, formatOwnershipType } from '../types/mutations'
import { toaster } from '@/shared/lib/toaster'
import { EmptyState } from '@/shared/components/EmptyState'
import { useState } from 'react'

interface UnitMutationsHistoryProps {
  unitId: string
  onAddMutation: () => void
}

export function UnitMutationsHistory({ unitId, onAddMutation }: UnitMutationsHistoryProps) {
  const { data: mutations, isLoading } = useUnitMutations(unitId)
  const deleteMutation = useDeleteUnitMutation()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [mutationToDelete, setMutationToDelete] = useState<string | null>(null)

  const handleDeleteClick = (mutationId: string) => {
    setMutationToDelete(mutationId)
    setDeleteDialogOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!mutationToDelete) return

    try {
      await deleteMutation.mutateAsync({ mutationId: mutationToDelete, unitId })
      toaster.create({
        title: 'Mutation supprimée',
        description: 'La mutation a été supprimée avec succès',
        type: 'success',
      })
      setDeleteDialogOpen(false)
      setMutationToDelete(null)
    } catch (error) {
      console.error('Error deleting mutation:', error)
      toaster.create({
        title: 'Erreur',
        description: error instanceof Error ? error.message : 'Une erreur est survenue lors de la suppression',
        type: 'error',
      })
    }
  }

  if (isLoading) {
    return (
      <Box py={8} textAlign="center">
        <Spinner size="lg" colorPalette="brand" />
        <Text mt={3} fontSize="sm" color="gray.600">
          Chargement de l'historique...
        </Text>
      </Box>
    )
  }

  if (!mutations || mutations.length === 0) {
    return (
      <VStack align="stretch" gap={4}>
        <HStack justify="space-between">
          <Text fontSize="md" fontWeight="bold">
            Historique des mutations
          </Text>
          <Button
            onClick={onAddMutation}
            size="sm"
            colorPalette="brand"
          >
            <Plus size={14} />
            Ajouter une mutation
          </Button>
        </HStack>

        <EmptyState
          title="Aucune mutation enregistrée"
          description="L'historique des changements de propriétaires apparaîtra ici"
        />
      </VStack>
    )
  }

  return (
    <VStack align="stretch" gap={4}>
      <HStack justify="space-between">
        <Text fontSize="md" fontWeight="bold">
          Historique des mutations ({mutations.length})
        </Text>
        <Button
          onClick={onAddMutation}
          size="sm"
          colorPalette="brand"
        >
          <Plus size={14} />
          Ajouter une mutation
        </Button>
      </HStack>

      <VStack align="stretch" gap={3}>
        {mutations.map((mutation) => {
          const metadata = getMutationTypeMetadata(mutation.mutation_type as never)

          return (
            <Box
              key={mutation.id}
              p={4}
              borderWidth="1px"
              borderRadius="md"
              borderColor="gray.200"
              bg="white"
              _hover={{ borderColor: 'brand.300', boxShadow: 'sm' }}
              transition="all 0.2s"
            >
              <VStack align="stretch" gap={3}>
                {/* En-tête */}
                <HStack justify="space-between">
                  <HStack gap={2}>
                    <ArrowRightLeft size={16} />
                    <Text fontSize="sm" fontWeight="bold">
                      {metadata.label}
                    </Text>
                  </HStack>
                  <HStack gap={1}>
                    <IconButton
                      variant="ghost"
                      size="xs"
                      colorPalette="red"
                      onClick={() => handleDeleteClick(mutation.id)}
                      aria-label="Supprimer"
                    >
                      <Trash2 size={14} />
                    </IconButton>
                  </HStack>
                </HStack>

                {/* Date */}
                <HStack gap={2}>
                  <Calendar size={14} />
                  <Text fontSize="xs" color="gray.600">
                    {new Date(mutation.mutation_date).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </Text>
                </HStack>

                {/* Sous-type */}
                {mutation.mutation_subtype && (
                  <Text fontSize="xs" color="gray.600" fontStyle="italic">
                    {mutation.mutation_subtype}
                  </Text>
                )}

                {/* Parties */}
                <HStack align="start" gap={4}>
                  {/* Anciens propriétaires */}
                  <Box flex={1}>
                    <Text fontSize="xs" fontWeight="bold" color="red.700" mb={1}>
                      Cédants
                    </Text>
                    <VStack align="stretch" gap={1}>
                      {mutation.former_parties.map((party) => (
                        <HStack key={party.id} gap={2} fontSize="xs">
                          <User size={12} />
                          <Text flex={1}>{formatOwnerName(party.owner)}</Text>
                          <Badge colorPalette="red" size="xs" variant="subtle">
                            {formatShare(party.share_numerator || 1, party.share_denominator || 1)}
                          </Badge>
                          {party.ownership_type !== 'full' && (
                            <Badge colorPalette="orange" size="xs" variant="outline">
                              {formatOwnershipType((party.ownership_type ?? "full") as "full" | "usufruct" | "bare")}
                            </Badge>
                          )}
                        </HStack>
                      ))}
                    </VStack>
                  </Box>

                  {/* Nouveaux propriétaires */}
                  <Box flex={1}>
                    <Text fontSize="xs" fontWeight="bold" color="green.700" mb={1}>
                      Acquéreurs
                    </Text>
                    <VStack align="stretch" gap={1}>
                      {mutation.new_parties.map((party) => (
                        <HStack key={party.id} gap={2} fontSize="xs">
                          <User size={12} />
                          <Text flex={1}>{formatOwnerName(party.owner)}</Text>
                          <Badge colorPalette="green" size="xs" variant="subtle">
                            {formatShare(party.share_numerator || 1, party.share_denominator || 1)}
                          </Badge>
                          {party.ownership_type !== 'full' && (
                            <Badge colorPalette="orange" size="xs" variant="outline">
                              {formatOwnershipType((party.ownership_type ?? "full") as "full" | "usufruct" | "bare")}
                            </Badge>
                          )}
                        </HStack>
                      ))}
                    </VStack>
                  </Box>
                </HStack>

                {/* Documents joints */}
                {mutation.documents && mutation.documents.length > 0 && (
                  <Box pt={2} borderTopWidth="1px" borderColor="gray.100">
                    <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={2}>
                      Documents joints ({mutation.documents.length})
                    </Text>
                    <VStack align="stretch" gap={1}>
                      {mutation.documents.map((doc) => (
                        <HStack
                          key={doc.id}
                          gap={2}
                          fontSize="xs"
                          p={2}
                          bg="gray.50"
                          borderRadius="sm"
                          _hover={{ bg: 'gray.100' }}
                        >
                          <File size={12} />
                          <Text flex={1} truncate>
                            {doc.file_name}
                          </Text>
                          <Text color="gray.500">
                            {(doc.file_size / 1024).toFixed(1)} KB
                          </Text>
                          <IconButton
                            size="xs"
                            variant="ghost"
                            aria-label="Télécharger"
                            onClick={() => {
                              toaster.create({
                                title: 'Fonctionnalité à venir',
                                description: 'Le téléchargement sera disponible avec la GED',
                                type: 'info',
                              })
                            }}
                          >
                            <Download size={12} />
                          </IconButton>
                        </HStack>
                      ))}
                    </VStack>
                  </Box>
                )}

                {/* Infos complémentaires */}
                {(mutation.notary_name || mutation.deed_reference || mutation.sale_price) && (
                  <Box pt={2} borderTopWidth="1px" borderColor="gray.100">
                    <VStack align="stretch" gap={1}>
                      {mutation.notary_name && (
                        <Text fontSize="xs" color="gray.600">
                          <strong>Notaire:</strong> {mutation.notary_name}
                        </Text>
                      )}
                      {mutation.deed_reference && (
                        <Text fontSize="xs" color="gray.600">
                          <strong>Acte:</strong> {mutation.deed_reference}
                          {mutation.deed_date &&
                            ` du ${new Date(mutation.deed_date).toLocaleDateString('fr-FR')}`}
                        </Text>
                      )}
                      {mutation.sale_price && (
                        <Text fontSize="xs" color="gray.600">
                          <strong>Prix:</strong> {mutation.sale_price.toLocaleString('fr-FR')} €
                        </Text>
                      )}
                    </VStack>
                  </Box>
                )}

                {/* Notes */}
                {mutation.notes && (
                  <Box pt={2} borderTopWidth="1px" borderColor="gray.100">
                    <Text fontSize="xs" color="gray.600" fontStyle="italic">
                      {mutation.notes}
                    </Text>
                  </Box>
                )}
              </VStack>
            </Box>
          )
        })}
      </VStack>

      {/* Dialog de confirmation de suppression */}
      <DialogRoot
        open={deleteDialogOpen}
        onOpenChange={(e) => {
          if (!e.open) {
            setDeleteDialogOpen(false)
            setMutationToDelete(null)
          }
        }}
      >
        <DialogBackdrop />
        <DialogPositioner>
          <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer cette mutation ?</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Text>Cette action est irréversible. L'historique de cette mutation sera définitivement supprimé.</Text>
          </DialogBody>
          <DialogFooter>
            <DialogActionTrigger asChild>
              <Button variant="outline">Annuler</Button>
            </DialogActionTrigger>
            <Button
              colorPalette="red"
              onClick={handleConfirmDelete}
              loading={deleteMutation.isPending}
            >
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
        </DialogPositioner>
      </DialogRoot>
    </VStack>
  )
}
